import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Crown, Package, ShoppingCart, Users, Check, AlertTriangle, CreditCard, Calendar, Loader2, Zap, Sparkles, ArrowRight, FileText, Download, ExternalLink, QrCode, Building, Wallet } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantSubscription() {
  const [data, setData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('qris');
  const [savingPayment, setSavingPayment] = useState(false);
  const [dialogMode, setDialogMode] = useState('upgrade'); // upgrade | renew
  // Map of feature code → { label, category } so we can render readable feature names
  // instead of raw codes like "physical" or "high_ticket".
  const [featureMeta, setFeatureMeta] = useState({});

  const fetch = async () => {
    try {
      const [subRes, pkgRes, featRes] = await Promise.all([
        api.get('/tenant/subscription'),
        api.get('/tenant/subscription/packages'),
        api.get('/features').catch(() => ({ data: { features: [] } })),
      ]);
      setData(subRes.data);
      // Sort packages by monthly price ascending so the upgrade dialog reads left-to-right.
      const sorted = (pkgRes.data.packages || []).sort((a, b) => (a.price_monthly || 0) - (b.price_monthly || 0));
      setPackages(sorted);
      const meta = {};
      for (const f of (featRes.data.features || [])) {
        meta[f.code] = { label: f.label || f.code, category: f.category || 'Lainnya' };
      }
      setFeatureMeta(meta);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const handleSubscribe = async () => {
    if (!selectedPackage) return toast.error('Pilih paket terlebih dahulu');
    setSubmitting(true);
    try {
      const r = await api.post('/tenant/subscription', {
        package_id: selectedPackage,
        billing_cycle: billingCycle,
        payment_method: 'transfer'
      });
      const provider = r.data.payment_provider || '';
      const instr = r.data.payment_instructions || '';
      if (provider === 'tripay' || provider === 'pakasir') {
        toast.success('Mengarahkan ke halaman pembayaran...');
        if (r.data.payment_url) window.open(r.data.payment_url, '_blank');
      } else if (provider === 'moota_transfer') {
        // Show transfer instructions inline so user knows exact amount + bank
        toast.success('Subscription dibuat. Lihat instruksi transfer di bawah.', { duration: 5000 });
        if (instr) window.alert('Instruksi Pembayaran:\n\n' + instr);
      } else {
        toast.warning('Pembayaran otomatis belum tersedia. Hubungi admin platform.', { duration: 8000 });
        if (instr) window.alert(instr);
      }
      setShowUpgradeDialog(false);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal membuat subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchInvoices = async () => {
    setInvoiceLoading(true);
    try {
      const res = await api.get('/tenant/subscription/invoices');
      setInvoices(res.data.invoices || res.data || []);
    } catch (e) {
      toast.error('Gagal memuat invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleOpenInvoices = () => {
    setShowInvoiceDialog(true);
    fetchInvoices();
  };

  const handleSavePaymentMethod = async () => {
    setSavingPayment(true);
    try {
      await api.put('/tenant/settings', { default_payment_method: paymentMethod });
      toast.success('Metode pembayaran default berhasil diubah');
      setShowPaymentDialog(false);
    } catch {
      toast.error('Gagal mengubah metode pembayaran');
    } finally {
      setSavingPayment(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const getUsagePercent = (current, max) => max === -1 ? 0 : Math.min((current / max) * 100, 100);
  const getUsageColor = (percent) => percent >= 90 ? 'bg-red-500' : percent >= 70 ? 'bg-amber-500' : 'bg-emerald-500';

  const subscriptionStatusLabels = {
    active: 'Aktif',
    trial: 'Trial',
    expired: 'Kedaluwarsa',
    canceled: 'Dibatalkan',
    pending: 'Menunggu Pembayaran',
    grace_period: 'Masa Tenggang',
  };

  const subscriptionStatusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    trial: 'bg-amber-100 text-amber-700',
    expired: 'bg-red-100 text-red-700',
    canceled: 'bg-slate-100 text-slate-700',
    pending: 'bg-blue-100 text-blue-700',
    grace_period: 'bg-amber-100 text-amber-800',
  };

  const getStatusLabel = (status) => subscriptionStatusLabels[status] || (status ? status.replace(/_/g, ' ') : 'Trial');
  const getStatusColor = (status) => subscriptionStatusColors[status] || subscriptionStatusColors.trial;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  const currentPackage = data?.package;
  const usage = data?.usage || {};
  const tenant = data?.tenant || {};
  const history = data?.history || [];

  const daysUntilExpiry = tenant?.subscription_end
    ? Math.ceil((new Date(tenant.subscription_end) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const canRenew = !!currentPackage && (
    tenant.subscription_status === 'grace_period'
    || tenant.subscription_status === 'expired'
    || tenant.subscription_status === 'active'
  );

  const openRenewDialog = () => {
    if (currentPackage?.id) {
      setSelectedPackage(currentPackage.id);
      setBillingCycle('monthly');
    }
    setDialogMode('renew');
    setShowUpgradeDialog(true);
  };

  const openUpgradeDialog = () => {
    setSelectedPackage(null);
    setDialogMode('upgrade');
    setShowUpgradeDialog(true);
  };

  const isRenewMode = dialogMode === 'renew';

  return (
    <div data-testid="tenant-subscription-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Subscription</h1>
          <p className="text-sm text-slate-500">Kelola paket dan subscription toko Anda</p>
        </div>
        <div className="flex items-center gap-2">
          {canRenew && (
            <Button onClick={openRenewDialog} variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="renew-btn">
              <Calendar size={16} className="mr-2" /> Perpanjang Paket
            </Button>
          )}
          <Button onClick={openUpgradeDialog} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="upgrade-btn">
            <Zap size={16} className="mr-2" /> Upgrade Paket
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current Plan */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-[#2D6CDF] to-[#10B981] p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Paket Saat Ini</p>
                  <h2 className="text-2xl font-bold mt-1">{currentPackage?.name || 'Free Trial'}</h2>
                </div>
                <Crown size={40} className="opacity-50" />
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Badge className={getStatusColor(tenant.subscription_status)}>
                  {getStatusLabel(tenant.subscription_status)}
                </Badge>
                {tenant.subscription_status === 'grace_period' && (
                  <span className="text-sm opacity-90">
                    Paket sudah berakhir — segera perpanjang agar toko tetap aktif.
                  </span>
                )}
                {tenant.subscription_end && (
                  <span className="text-sm opacity-80">
                    <Calendar size={14} className="inline mr-1" />
                    {tenant.subscription_status === 'grace_period' ? 'Batas perpanjangan' : 'Berakhir'}: {new Date(tenant.subscription_end).toLocaleDateString('id-ID')}
                    {daysUntilExpiry !== null && tenant.subscription_status === 'active' && daysUntilExpiry <= 14 && (
                      <span className="ml-1">({daysUntilExpiry} hari lagi)</span>
                    )}
                  </span>
                )}
              </div>
            </div>
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Pemakaian</h3>
              <div className="space-y-4">
                {Object.entries(usage).map(([key, val]) => {
                  const percent = getUsagePercent(val.current, val.max);
                  const isUnlimited = val.max === -1;
                  const isMonthly = val.period === 'monthly';
                  const labels = {
                    products: 'Produk',
                    orders: isMonthly ? 'Pesanan bulan ini' : 'Pesanan',
                    customers: 'Pelanggan',
                  };
                  const icons = { products: Package, orders: ShoppingCart, customers: Users };
                  const Icon = icons[key] || Package;
                  const resetDate = val.reset_at ? new Date(val.reset_at) : null;
                  const resetLabel = resetDate
                    ? resetDate.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
                    : null;
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-2 text-slate-600">
                          <Icon size={14} /> {labels[key] || key}
                          {isMonthly && (
                            <span className="text-[10px] uppercase font-semibold tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                              Bulanan
                            </span>
                          )}
                        </span>
                        <span className="font-medium">
                          {val.current} / {isUnlimited ? '∞' : fmt(val.max)}
                        </span>
                      </div>
                      {!isUnlimited && (
                        <Progress value={percent} className="h-2" indicatorClassName={getUsageColor(percent)} />
                      )}
                      {isUnlimited && <div className="h-2 bg-emerald-100 rounded-full" />}
                      {isMonthly && resetLabel && (
                        <p className="text-[11px] text-slate-500 mt-1">Reset otomatis pada {resetLabel}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {currentPackage?.features?.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <h3 className="font-semibold text-slate-900 mb-3">Fitur Termasuk</h3>
                  <div className="flex flex-wrap gap-2">
                    {currentPackage.features.map((f, i) => (
                      <Badge key={i} variant="outline" className="bg-[#2D6CDF]/5 text-[#2D6CDF] border-[#2D6CDF]/20">
                        <Check size={12} className="mr-1" /> {f}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Billing History */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Riwayat Pembayaran</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Tanggal</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Paket</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Periode</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Jumlah</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-3 px-4 text-slate-600">{new Date(h.created_at).toLocaleDateString('id-ID')}</td>
                      <td className="py-3 px-4 font-medium text-slate-900">{h.package_name}</td>
                      <td className="py-3 px-4 text-slate-600 capitalize">{h.billing_cycle}</td>
                      <td className="py-3 px-4 font-medium">Rp {fmt(h.amount)}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={getStatusColor(h.status)}>
                          {getStatusLabel(h.status)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {!history.length && (
                    <tr><td colSpan={5} className="py-8 text-center text-slate-400">Belum ada riwayat</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Aksi Cepat</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {canRenew && (
                <Button variant="outline" className="w-full justify-start border-amber-300 text-amber-800 hover:bg-amber-50" onClick={openRenewDialog}>
                  <Calendar size={16} className="mr-2 text-amber-600" /> Perpanjang Paket
                </Button>
              )}
              <Button variant="outline" className="w-full justify-start" onClick={openUpgradeDialog}>
                <Zap size={16} className="mr-2 text-[#2D6CDF]" /> Upgrade Paket
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => setShowPaymentDialog(true)}>
                <CreditCard size={16} className="mr-2 text-emerald-500" /> Ubah Metode Bayar
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={handleOpenInvoices}>
                <Calendar size={16} className="mr-2 text-slate-500" /> Lihat Invoice
              </Button>
            </CardContent>
          </Card>

          {/* Grace period warning */}
          {tenant.subscription_status === 'grace_period' && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-medium text-amber-900">Masa Tenggang Aktif</h3>
                    <p className="text-sm text-amber-800 mt-1">
                      Langganan paket Anda sudah melewati tanggal berakhir. Anda masih bisa menggunakan toko sementara waktu — segera klik <strong>Perpanjang Paket</strong> untuk melanjutkan layanan tanpa gangguan.
                    </p>
                    <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700" onClick={openRenewDialog}>
                      Perpanjang Sekarang
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warnings */}
          {Object.entries(usage).some(([_, v]) => v.max !== -1 && (v.current / v.max) >= 0.8) && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-amber-800">Mendekati Limit</h3>
                    <p className="text-sm text-amber-700 mt-1">
                      Beberapa fitur mendekati batas maksimal. Upgrade paket untuk menambah kapasitas.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Help */}
          <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="p-4 text-center">
              <Sparkles size={24} className="mx-auto text-[#2D6CDF] mb-2" />
              <p className="text-sm text-slate-600">Butuh bantuan memilih paket?</p>
              <Button variant="link" className="text-[#2D6CDF] p-0 h-auto mt-1">
                Hubungi Sales <ArrowRight size={14} className="ml-1" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={(open) => { setShowUpgradeDialog(open); if (!open) setDialogMode('upgrade'); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isRenewMode ? 'Perpanjang Paket' : 'Pilih Paket'}</DialogTitle>
          </DialogHeader>
          {isRenewMode && currentPackage && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Perpanjang paket <strong>{currentPackage.name}</strong> dengan periode billing yang Anda pilih, lalu lanjut ke pembayaran.
            </div>
          )}
          <div className="space-y-6">
            {/* Billing Cycle */}
            <div className="flex items-center justify-center gap-4 p-4 bg-slate-50 rounded-lg">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${billingCycle === 'monthly' ? 'bg-[#2D6CDF] text-white' : 'bg-white text-slate-600'}`}
              >
                Bulanan
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${billingCycle === 'yearly' ? 'bg-[#2D6CDF] text-white' : 'bg-white text-slate-600'}`}
              >
                Tahunan <Badge className="ml-2 bg-emerald-500">Hemat 20%</Badge>
              </button>
            </div>

            {/* Packages — render all features grouped by category with readable labels.
                Highlight features that are NEW relative to the tenant's current package
                so the differentiation is visible at a glance. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {packages.map((pkg, idx) => {
                const price = billingCycle === 'monthly' ? pkg.price_monthly : pkg.price_yearly;
                const isSelected = selectedPackage === pkg.id;
                const isCurrent = currentPackage?.id === pkg.id;
                const currentFeatures = new Set((currentPackage?.features) || []);
                const pkgFeatures = pkg.features || [];
                const newFeatures = pkgFeatures.filter((f) => !currentFeatures.has(f));
                // Determine if downgrade (price lower than current)
                const currentPrice = currentPackage?.price_monthly || 0;
                const isDowngrade = !isCurrent && currentPackage && (pkg.price_monthly || 0) < currentPrice;
                const allowSelect = !isDowngrade && (!isCurrent || isRenewMode);
                // Group features by category for visual structure
                const grouped = pkgFeatures.reduce((acc, code) => {
                  const m = featureMeta[code] || { label: code, category: 'Lainnya' };
                  if (!acc[m.category]) acc[m.category] = [];
                  acc[m.category].push({ code, label: m.label, isNew: !currentFeatures.has(code) });
                  return acc;
                }, {});
                // Higher tier = highlight popular (middle card visually)
                const isPopular = pkg.is_popular || (packages.length >= 3 && idx === Math.floor(packages.length / 2));

                return (
                  <div
                    key={pkg.id}
                    onClick={() => allowSelect && setSelectedPackage(pkg.id)}
                    className={`relative p-5 rounded-xl border-2 transition-all flex flex-col ${
                      isCurrent && !isRenewMode ? 'border-emerald-300 bg-emerald-50/50 cursor-default' :
                      isCurrent && isRenewMode && isSelected ? 'border-amber-400 bg-amber-50/80 shadow-md cursor-pointer' :
                      isCurrent && isRenewMode ? 'border-emerald-300 bg-emerald-50/50 cursor-pointer hover:border-amber-400' :
                      isDowngrade ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed' :
                      isSelected ? 'border-[#2D6CDF] bg-[#2D6CDF]/5 shadow-md cursor-pointer' :
                      isPopular ? 'border-[#2D6CDF]/40 bg-white hover:border-[#2D6CDF] cursor-pointer shadow-sm' :
                      'border-slate-200 hover:border-slate-400 cursor-pointer bg-white'
                    }`}
                    data-testid={`package-option-${pkg.id}`}
                  >
                    {isPopular && !isCurrent && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-violet-600 to-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow">
                        ⭐ POPULER
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-slate-900 text-lg">{pkg.name}</h3>
                      {isCurrent && isRenewMode && isSelected && <Badge className="bg-amber-500">Dipilih</Badge>}
                      {isCurrent && (!isRenewMode || !isSelected) && <Badge className="bg-emerald-500">Aktif</Badge>}
                      {isDowngrade && <Badge variant="outline" className="text-slate-500">Downgrade</Badge>}
                    </div>
                    {pkg.description && (
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{pkg.description}</p>
                    )}
                    <div className="mb-4">
                      <p className="text-3xl font-bold text-[#2D6CDF]">
                        Rp {fmt(price)}
                      </p>
                      <span className="text-xs text-slate-500">per {billingCycle === 'monthly' ? 'bulan' : 'tahun'}</span>
                      {billingCycle === 'yearly' && pkg.price_monthly > 0 && (
                        <p className="text-[11px] text-emerald-600 mt-0.5">
                          ≈ Rp {fmt(Math.round((pkg.price_yearly || 0) / 12))}/bln (hemat {Math.round((1 - (pkg.price_yearly / 12 / pkg.price_monthly)) * 100)}%)
                        </p>
                      )}
                    </div>

                    {/* Capacity row */}
                    <div className="space-y-1.5 text-sm border-t border-slate-100 pt-3 mb-3">
                      <p className="flex items-center gap-2">
                        <Package size={14} className="text-slate-400" />
                        <span className="text-slate-700">{pkg.max_products === -1 ? <strong className="text-[#2D6CDF]">Unlimited</strong> : fmt(pkg.max_products)} produk</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <ShoppingCart size={14} className="text-slate-400" />
                        <span className="text-slate-700">{pkg.max_orders === -1 ? <strong className="text-[#2D6CDF]">Unlimited</strong> : fmt(pkg.max_orders)} order/bln</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <Users size={14} className="text-slate-400" />
                        <span className="text-slate-700">{pkg.max_customers === -1 ? <strong className="text-[#2D6CDF]">Unlimited</strong> : fmt(pkg.max_customers)} customer</span>
                      </p>
                    </div>

                    {/* Features grouped by category */}
                    {pkgFeatures.length > 0 && (
                      <div className="border-t border-slate-100 pt-3 flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Fitur ({pkgFeatures.length})</p>
                          {newFeatures.length > 0 && !isCurrent && (
                            <Badge className="bg-amber-100 text-amber-800 text-[9px]">+{newFeatures.length} baru</Badge>
                          )}
                        </div>
                        <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                          {Object.entries(grouped).map(([cat, items]) => (
                            <div key={cat}>
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{cat}</p>
                              <div className="space-y-0.5">
                                {items.map((it) => (
                                  <p key={it.code} className="text-xs text-slate-700 flex items-start gap-1.5">
                                    <Check size={12} className={`mt-0.5 flex-shrink-0 ${it.isNew ? 'text-amber-500' : 'text-emerald-500'}`} />
                                    <span className="flex-1">{it.label}</span>
                                    {it.isNew && !isCurrent && (
                                      <span className="text-[9px] text-amber-700 font-semibold bg-amber-50 px-1 rounded">BARU</span>
                                    )}
                                  </p>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {currentPackage && (
              <p className="text-[11px] text-center text-slate-500">
                <strong className="text-amber-600">Tip:</strong> Fitur dengan badge <span className="text-amber-700">BARU</span> belum ada di paket aktif Anda. Downgrade dinonaktifkan untuk menghindari kehilangan data.
              </p>
            )}

            <Button onClick={handleSubscribe} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={submitting || !selectedPackage} data-testid="confirm-upgrade-btn">
              {submitting && <Loader2 size={16} className="animate-spin mr-2" />}
              {submitting ? 'Memproses...' : (isRenewMode ? 'Perpanjang & Bayar' : 'Lanjut ke Pembayaran')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText size={18} /> Riwayat Invoice</DialogTitle>
          </DialogHeader>
          {invoiceLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#2D6CDF]" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText size={40} className="mx-auto mb-3 opacity-40" />
              <p>Belum ada invoice</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((inv, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm font-semibold text-slate-800">{inv.invoice_number || inv.id || `INV-${i + 1}`}</span>
                    <Badge className={getStatusColor(inv.status)}>
                      {getStatusLabel(inv.status)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="space-y-1 text-slate-600">
                      <p>Paket: <span className="font-medium text-slate-800">{inv.package_name || '-'}</span></p>
                      <p>Periode: <span className="capitalize">{inv.billing_cycle || '-'}</span></p>
                      <p>Tanggal: {inv.created_at ? new Date(inv.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#2D6CDF]">Rp {fmt(inv.amount)}</p>
                      {inv.payment_url && inv.status === 'pending' && (
                        <a href={inv.payment_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#2D6CDF] hover:underline mt-1">
                          <ExternalLink size={12} /> Bayar
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Method Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard size={18} /> Ubah Metode Pembayaran</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-4">Pilih metode pembayaran default untuk perpanjangan subscription.</p>
          <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2">
            {[
              { value: 'qris', label: 'QRIS', desc: 'Scan QR — semua bank & e-wallet', icon: QrCode },
              { value: 'bni_va', label: 'BNI Virtual Account', desc: 'Transfer via BNI VA', icon: Building },
              { value: 'bri_va', label: 'BRI Virtual Account', desc: 'Transfer via BRI VA', icon: Building },
              { value: 'cimb_niaga_va', label: 'CIMB Niaga VA', desc: 'Transfer via CIMB VA', icon: Building },
              { value: 'dana', label: 'DANA', desc: 'E-Wallet DANA', icon: Wallet },
              { value: 'gopay', label: 'GoPay', desc: 'E-Wallet GoPay', icon: Wallet },
            ].map(pm => {
              const PMIcon = pm.icon;
              const isActive = paymentMethod === pm.value;
              return (
                <label key={pm.value} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                  isActive ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'
                }`}>
                  <RadioGroupItem value={pm.value} />
                  <PMIcon size={18} className="text-slate-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{pm.label}</p>
                    <p className="text-xs text-slate-500">{pm.desc}</p>
                  </div>
                </label>
              );
            })}
          </RadioGroup>
          <Button onClick={handleSavePaymentMethod} className="w-full mt-4 bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={savingPayment}>
            {savingPayment && <Loader2 size={16} className="animate-spin mr-2" />}
            {savingPayment ? 'Menyimpan...' : 'Simpan Metode Pembayaran'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
